import type React from 'react';
import { buttonStyles } from '../tailgrids/core/button.js';
import { Spinner } from '../tailgrids/core/spinner/index.js';
import { cn } from '../../utils/cn.js';

type ButtonVariant = 'primary' | 'secondary' | 'success' | 'danger' | 'ghost';
type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Shows a spinner next to the label and blocks presses while an action runs. */
  loading?: boolean;
  /** Square button holding only an icon; give it an `aria-label`. */
  iconOnly?: boolean;
}

/** Our variants on NextAdmin's button styles: one filled primary action, outline secondary, quiet ghost. */
const STYLE: Record<
  ButtonVariant,
  { variant: 'primary' | 'danger' | 'success' | 'ghost'; appearance: 'fill' | 'outline' | 'ghost' }
> = {
  primary: { variant: 'primary', appearance: 'fill' },
  secondary: { variant: 'primary', appearance: 'outline' },
  success: { variant: 'success', appearance: 'fill' },
  danger: { variant: 'danger', appearance: 'fill' },
  ghost: { variant: 'ghost', appearance: 'ghost' },
};

/**
 * The app's button (native `<button>`, so `onClick`/`type`/`form` work as usual) styled by the vendored
 * TailGrids `buttonStyles`. For react-aria composition (menus, dialogs) use `tailgrids/core/button` directly.
 */
export function Button({
  children,
  variant = 'primary',
  size = 'md',
  loading = false,
  iconOnly = false,
  disabled,
  className,
  type = 'button',
  ...props
}: ButtonProps) {
  return (
    <button
      type={type}
      className={cn(
        buttonStyles({ ...STYLE[variant], size, iconOnly }),
        'disabled:cursor-not-allowed disabled:opacity-60',
        className,
      )}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {loading && <Spinner size="sm" className="size-4" />}
      {children}
    </button>
  );
}
